import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Plus,
  MoreHorizontal,
  MessageCircle,
  Paperclip,
  Flag,
  X,
  RefreshCw,
} from "lucide-react";
import api from "../api";

// ---------------------------------------------------------------------------
// API routes — adjust these two constants if your Express routes for the
// TaskBoard model use different paths (e.g. "/taskboards" instead of
// "/tasks"). Everything else in this file stays the same either way.
// ---------------------------------------------------------------------------
const LIST_ENDPOINT = "/tasks"; // GET (flat list of TaskBoard docs), POST (create)
const ITEM_ENDPOINT = (id) => `/tasks/${id}`; // PATCH / DELETE a single task

// ---------------------------------------------------------------------------
// Design tokens (kept identical to ProjectManagementDashboard for consistency)
// ---------------------------------------------------------------------------
const colors = {
  navy: "#0E1B3D",
  navyBorder: "rgba(255,255,255,0.08)",
  bg: "#F4F6FB",
  card: "#FFFFFF",
  border: "#EBEEF5",
  text: "#101828",
  subtext: "#667085",
  blue: "#3B6BF6",
  green: "#17A24A",
  orange: "#F59E0B",
  purple: "#7C4CE0",
  teal: "#0DA5A5",
  red: "#E0483F",
};

const shadow = "0 2px 10px rgba(16,24,40,0.06)";

const PRIORITY_STYLES = {
  High: { bg: "#FDEBEA", color: colors.red },
  Medium: { bg: "#FDF0E1", color: colors.orange },
  Low: { bg: "#E8F7EE", color: colors.green },
};

// Column ids MUST match the `status` enum in the TaskBoard schema exactly:
// ["todo", "inprogress", "review", "done"]
const BOARD_COLUMNS = [
  { id: "todo", title: "To Do", color: "#9AA0AC" },
  { id: "inprogress", title: "In Progress", color: colors.blue },
  { id: "review", title: "In Review", color: colors.orange },
  { id: "done", title: "Done", color: colors.green },
];

const AVATAR_COLORS = [
  colors.blue,
  colors.purple,
  colors.teal,
  colors.orange,
  colors.green,
];
function avatarColor(initials) {
  if (!initials) return colors.subtext;
  return AVATAR_COLORS[initials.charCodeAt(0) % AVATAR_COLORS.length];
}

// ---------------------------------------------------------------------------
// Maps a raw TaskBoard document straight to what the card UI needs.
// Field names here match the mongoose schema 1:1.
// ---------------------------------------------------------------------------
function normalizeTask(raw) {
  return {
    id: raw._id ?? raw.id,
    title: raw.title,
    project: raw.project || "",
    status: raw.status || "todo",
    priority: raw.priority || "Medium",
    dueDate: raw.dueDate || "—",
    assignee: raw.assignee || "—",
    comments: raw.comments ?? 0,
    attachments: raw.attachments ?? 0,
    checklist: raw.checklist ?? { done: 0, total: 0 },
  };
}

// Groups a flat array of TaskBoard documents into the 4 board columns.
function groupIntoColumns(rawTasks) {
  const grouped = BOARD_COLUMNS.map((c) => ({ ...c, tasks: [] }));
  rawTasks.forEach((raw) => {
    const task = normalizeTask(raw);
    const target = grouped.find((c) => c.id === task.status) || grouped[0];
    target.tasks.push(task);
  });
  return grouped;
}

// ---------------------------------------------------------------------------
// Task card
// ---------------------------------------------------------------------------
function TaskCard({ task, onDragStart }) {
  const pStyle = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Medium;
  const pct = task.checklist.total
    ? Math.round((task.checklist.done / task.checklist.total) * 100)
    : 0;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task.id)}
      style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        padding: 14,
        marginBottom: 12,
        cursor: "grab",
        boxShadow: shadow,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            padding: "3px 9px",
            borderRadius: 20,
            background: pStyle.bg,
            color: pStyle.color,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Flag size={10} />
          {task.priority}
        </span>
        <MoreHorizontal
          size={15}
          color={colors.subtext}
          style={{ cursor: "pointer" }}
        />
      </div>

      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: colors.text,
          marginBottom: 6,
          lineHeight: 1.35,
        }}
      >
        {task.title}
      </div>
      {task.project && (
        <div
          style={{ fontSize: 11.5, color: colors.subtext, marginBottom: 10 }}
        >
          {task.project}
        </div>
      )}

      {task.checklist.total > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 10.5,
              color: colors.subtext,
              marginBottom: 4,
            }}
          >
            <span>Checklist</span>
            <span>
              {task.checklist.done}/{task.checklist.total}
            </span>
          </div>
          <div
            style={{
              width: "100%",
              height: 5,
              background: "#EAECF2",
              borderRadius: 6,
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: 5,
                background: colors.blue,
                borderRadius: 6,
              }}
            />
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            color: colors.subtext,
            fontSize: 11,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Calendar size={12} /> {task.dueDate}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <MessageCircle size={12} /> {task.comments}
          </span>
          {task.attachments > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Paperclip size={12} /> {task.attachments}
            </span>
          )}
        </div>
        <div
          title={task.assignee}
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: avatarColor(task.assignee),
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {task.assignee}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Column
// ---------------------------------------------------------------------------
function Column({ column, onDragStart, onDrop, onAddTask }) {
  const [dragOver, setDragOver] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const submitDraft = () => {
    if (draft.trim()) onAddTask(column.id, draft.trim());
    setDraft("");
    setAdding(false);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        setDragOver(false);
        onDrop(e, column.id);
      }}
      style={{
        background: dragOver ? "#EEF2FE" : colors.bg,
        borderRadius: 14,
        padding: 14,
        width: 300,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 220px)",
        transition: "background .12s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
          padding: "0 2px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: column.color,
            }}
          />
          <span style={{ fontSize: 13.5, fontWeight: 700, color: colors.text }}>
            {column.title}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: colors.subtext,
              background: colors.card,
              borderRadius: 10,
              padding: "1px 8px",
            }}
          >
            {column.tasks.length}
          </span>
        </div>
        <button
          onClick={() => setAdding(true)}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: colors.subtext,
          }}
          title="Add task"
        >
          <Plus size={16} />
        </button>
      </div>

      <div style={{ overflowY: "auto", paddingRight: 2 }}>
        {column.tasks.map((task) => (
          <TaskCard key={task.id} task={task} onDragStart={onDragStart} />
        ))}

        {column.tasks.length === 0 && !adding && (
          <div
            style={{
              fontSize: 12,
              color: colors.subtext,
              textAlign: "center",
              padding: "18px 0",
            }}
          >
            No tasks yet
          </div>
        )}

        {adding && (
          <div
            style={{
              background: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: 10,
              marginBottom: 12,
              boxShadow: shadow,
            }}
          >
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submitDraft();
                }
                if (e.key === "Escape") {
                  setAdding(false);
                  setDraft("");
                }
              }}
              placeholder="Task title..."
              rows={2}
              style={{
                width: "100%",
                border: "none",
                outline: "none",
                resize: "none",
                fontSize: 13,
                fontFamily: "inherit",
                marginBottom: 8,
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={submitDraft}
                style={{
                  background: colors.blue,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Add
              </button>
              <button
                onClick={() => {
                  setAdding(false);
                  setDraft("");
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: colors.subtext,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {!adding && (
          <button
            onClick={() => setAdding(true)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              border: `1px dashed ${colors.border}`,
              background: "transparent",
              borderRadius: 12,
              padding: "10px 0",
              fontSize: 12.5,
              color: colors.subtext,
              cursor: "pointer",
            }}
          >
            <Plus size={14} /> Add task
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Task modal — triggered by the top-right "New Task" button.
// Lets you actually type a title and pick which column it goes into,
// instead of the old hardcoded "New task" title.
// ---------------------------------------------------------------------------
function NewTaskModal({ columns, defaultColumnId, onClose, onCreate }) {
  const [title, setTitle] = useState("");
  const [columnId, setColumnId] = useState(defaultColumnId);
  const [priority, setPriority] = useState("Medium");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    await onCreate(columnId, title.trim(), priority);
    setSubmitting(false);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(16,24,40,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: 14,
          padding: 22,
          width: 380,
          maxWidth: "90vw",
          boxShadow: "0 12px 32px rgba(16,24,40,0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: colors.text,
            }}
          >
            New Task
          </h3>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: colors.subtext,
            }}
          >
            <X size={18} />
          </button>
        </div>

        <label
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: colors.subtext,
            display: "block",
            marginBottom: 6,
          }}
        >
          Title
        </label>
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
            if (e.key === "Escape") onClose();
          }}
          placeholder="e.g. Finalize floor plan for Villa 32"
          style={{
            width: "100%",
            border: `1px solid ${colors.border}`,
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 13.5,
            marginBottom: 14,
            outline: "none",
            boxSizing: "border-box",
          }}
        />

        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: colors.subtext,
                display: "block",
                marginBottom: 6,
              }}
            >
              Column
            </label>
            <select
              value={columnId}
              onChange={(e) => setColumnId(e.target.value)}
              style={{
                width: "100%",
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 13,
                boxSizing: "border-box",
              }}
            >
              {columns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: colors.subtext,
                display: "block",
                marginBottom: 6,
              }}
            >
              Priority
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              style={{
                width: "100%",
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 13,
                boxSizing: "border-box",
              }}
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: `1px solid ${colors.border}`,
              borderRadius: 10,
              padding: "9px 16px",
              fontSize: 13,
              color: colors.text,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || submitting}
            style={{
              background: colors.blue,
              border: "none",
              borderRadius: 10,
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: "#fff",
              cursor: !title.trim() || submitting ? "not-allowed" : "pointer",
              opacity: !title.trim() || submitting ? 0.6 : 1,
            }}
          >
            {submitting ? "Creating…" : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main board
// ---------------------------------------------------------------------------
const TaskBoardKanban = () => {
  const [columns, setColumns] = useState(
    BOARD_COLUMNS.map((c) => ({ ...c, tasks: [] })),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectFilter, setProjectFilter] = useState("All Projects");
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);

  useEffect(() => {
    fetchBoard();
  }, []);

  const fetchBoard = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(LIST_ENDPOINT);
      const rawTasks = Array.isArray(res.data) ? res.data : res.data?.tasks;
      if (!Array.isArray(rawTasks)) {
        setError(`Unexpected response format from ${LIST_ENDPOINT}.`);
        return;
      }
      setColumns(groupIntoColumns(rawTasks));
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.status === 404
          ? `Endpoint not found (${LIST_ENDPOINT}). Check your API route.`
          : "Couldn't load tasks. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const projectOptions = useMemo(() => {
    const set = new Set(["All Projects"]);
    columns.forEach((col) =>
      col.tasks.forEach((t) => t.project && set.add(t.project)),
    );
    return Array.from(set);
  }, [columns]);

  const visibleColumns = useMemo(() => {
    if (projectFilter === "All Projects") return columns;
    return columns.map((col) => ({
      ...col,
      tasks: col.tasks.filter((t) => t.project === projectFilter),
    }));
  }, [columns, projectFilter]);

  const handleDragStart = (e, taskId) => {
    e.dataTransfer.setData("text/plain", taskId);
  };

  // Optimistically moves the task locally, then persists the new `status`
  // to the API. Rolls back by refetching if the PATCH fails.
  const handleDrop = async (e, targetColumnId) => {
    const taskId = e.dataTransfer.getData("text/plain");
    let movedTask = null;
    let sourceColumnId = null;

    setColumns((prev) => {
      const withoutTask = prev.map((col) => {
        const found = col.tasks.find((t) => t.id === taskId);
        if (found) {
          movedTask = found;
          sourceColumnId = col.id;
        }
        return { ...col, tasks: col.tasks.filter((t) => t.id !== taskId) };
      });
      if (!movedTask || sourceColumnId === targetColumnId) return prev;
      return withoutTask.map((col) =>
        col.id === targetColumnId
          ? {
              ...col,
              tasks: [{ ...movedTask, status: targetColumnId }, ...col.tasks],
            }
          : col,
      );
    });

    if (!movedTask || sourceColumnId === targetColumnId) return;

    try {
      await api.patch(ITEM_ENDPOINT(taskId), { status: targetColumnId });
    } catch (err) {
      console.error(err);
      setError("Couldn't save the move. Reverting.");
      fetchBoard();
    }
  };

  // Creates a task with every field the TaskBoard schema expects.
  // Called from both the inline per-column "+ Add task" box and the
  // top "New Task" modal.
  const handleAddTask = async (columnId, title, priority = "Medium") => {
    try {
      const payload = {
        title,
        status: columnId,
        project: projectFilter === "All Projects" ? "" : projectFilter,
        priority,
        dueDate: "",
        assignee: "",
        comments: 0,
        attachments: 0,
        checklist: { done: 0, total: 0 },
      };
      const res = await api.post(LIST_ENDPOINT, payload);
      const newTask = normalizeTask(res.data);
      setColumns((prev) =>
        prev.map((col) =>
          col.id === columnId
            ? { ...col, tasks: [newTask, ...col.tasks] }
            : col,
        ),
      );
      setShowNewTaskModal(false);
    } catch (err) {
      console.error(err);
      setError("Couldn't create the task. Please try again.");
    }
  };

  return (
    <div
      style={{
        background: colors.bg,
        fontFamily: "Inter, system-ui, sans-serif",
        minHeight: "100%",
      }}
    >
      <main style={{ padding: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 20,
            flexWrap: "wrap",
            gap: 14,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 24,
                fontWeight: 700,
                color: colors.text,
                margin: 0,
              }}
            >
              Task Board
            </h1>
            <p style={{ fontSize: 13.5, color: colors.subtext, marginTop: 4 }}>
              Drag tasks between columns to update their status.
            </p>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              style={{
                background: colors.card,
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: "10px 14px",
                fontSize: 13,
                color: colors.text,
                cursor: "pointer",
                boxShadow: shadow,
              }}
            >
              {projectOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <button
              onClick={fetchBoard}
              title="Refresh"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: colors.card,
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: "10px 14px",
                fontSize: 13,
                color: colors.text,
                cursor: "pointer",
                boxShadow: shadow,
              }}
            >
              <RefreshCw size={14} />
            </button>
            <button
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: colors.blue,
                border: "none",
                borderRadius: 10,
                padding: "10px 18px",
                fontSize: 13.5,
                fontWeight: 600,
                color: "#fff",
                cursor: "pointer",
                boxShadow: shadow,
              }}
              onClick={() => setShowNewTaskModal(true)}
            >
              <Plus size={15} />
              New Task
            </button>
          </div>
        </div>

        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#FDEBEA",
              color: "#9A342B",
              fontSize: 13,
              padding: "10px 14px",
              borderRadius: 10,
              marginBottom: 18,
            }}
          >
            <span>{error}</span>
            <button
              onClick={fetchBoard}
              style={{
                background: "transparent",
                border: "none",
                color: "#9A342B",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: 12.5,
              }}
            >
              Retry
            </button>
          </div>
        )}

        {loading ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: colors.subtext,
              fontSize: 13.5,
            }}
          >
            Loading board…
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              gap: 18,
              overflowX: "auto",
              paddingBottom: 10,
            }}
          >
            {visibleColumns.map((col) => (
              <Column
                key={col.id}
                column={col}
                onDragStart={handleDragStart}
                onDrop={handleDrop}
                onAddTask={handleAddTask}
              />
            ))}
          </div>
        )}
      </main>

      {showNewTaskModal && (
        <NewTaskModal
          columns={BOARD_COLUMNS}
          defaultColumnId={BOARD_COLUMNS[0].id}
          onClose={() => setShowNewTaskModal(false)}
          onCreate={handleAddTask}
        />
      )}
    </div>
  );
};

export default TaskBoardKanban;
